import { FC, useRef, useState } from 'react';
import { ICaremapNoteResponse, NotesClient } from '../model/NotesClient';
import { IExtractedFields, IRecording } from '../../appointments/recording/RecordingState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { useNudgeStorage } from '../../utils/useNudgeStorage';
import { SelectableText } from '../../components/SelectableText';
import { IonButton, IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import { createUseStyles } from 'react-jss';
import { DuoUserIcon, DuoClipboardListIcon, DuoHandDotsIcon, DuoPrescriptionBottleIcon, DuoLocationDotIcon } from '../../theme/Icons';
import { IconComponent } from '../../theme/Icons';

const FIELD_CONFIG: { key: keyof IExtractedFields; label: string; Icon: IconComponent }[] = [
    { key: 'careRecipientName', label: 'Care Recipient', Icon: DuoUserIcon },
    { key: 'diagnoses', label: 'Diagnoses', Icon: DuoClipboardListIcon },
    { key: 'allergies', label: 'Allergies', Icon: DuoHandDotsIcon },
    { key: 'medications', label: 'Medications', Icon: DuoPrescriptionBottleIcon },
    { key: 'address', label: 'Location', Icon: DuoLocationDotIcon },
];

const hasExtractedFields = (fields?: IExtractedFields): boolean => {
    if (!fields) {
        return false;
    }
    return FIELD_CONFIG.some(({ key }) => {
        const value = fields[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    });
};

const formatFieldValue = (value: string | string[] | undefined): string | undefined => {
    if (!value) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : undefined;
    }
    return value;
};

const useStyles = createUseStyles({
    '@keyframes hide': {
        from: { opacity: 1 },
        to: { opacity: 0 }
    },

    hiding: {
        animation: '$hide ease 0.5s',
        opacity: 0
    },

    container: {
        margin: '20px 0',
    },

    bubble: {
        background: 'rgba(var(--ion-color-primary-rgb), 0.1)',
        borderRadius: 16,
        padding: '12px 16px',
        position: 'relative' as const,
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },

    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0,
    },

    headerInfo: {
        flex: 1,
    },

    name: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-text-color)',
    },

    closeButton: {
        alignSelf: 'flex-start',
        '--padding-start': '2px',
        '--padding-end': '2px',
    },

    commentText: {
        fontSize: 14,
        lineHeight: '1.5',
        color: 'var(--ion-color-step-900)',
        marginBottom: 10,
    },

    fieldsTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-text-color)',
        marginBottom: 6,
    },

    fieldsCard: {
        background: 'white',
        borderRadius: 8,
        padding: '8px 12px',
        marginBottom: 10,
    },

    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid var(--ion-color-step-100)',
        '&:last-child': {
            borderBottom: 'none',
        },
    },

    fieldIcon: {
        width: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        textAlign: 'center' as const,
    },

    fieldLabel: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        fontWeight: 400,
        width: 100,
        flexShrink: 0,
    },

    fieldValue: {
        fontSize: 13,
        color: 'var(--ion-text-color)',
        fontWeight: 600,
        flex: 1,
        textAlign: 'right' as const,
    },

    profileLink: {
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
    },

    buttons: {
        padding: '4px 50px 0',
    },
});

type IBeaCommentBubbleProps = {
    note: ICaremapNoteResponse;
};

export const BeaCommentBubble: FC<IBeaCommentBubbleProps> = ({ note }) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const notesClient = useInstance(NotesClient);

    const [hiding, setHiding] = useState(false);
    const [firstViewDismissed, setFirstViewDismissed] = useState(false);
    const seenMarkedRef = useRef(false);

    // Lock in the server-side seen state at mount time. Within this session,
    // the first-view-to-followup transition is driven by user dismiss (firstViewDismissed),
    // not by server updates. This prevents mid-session flicker when a server refresh
    // updates isBeaCommentSeen while the user is still reading the first view.
    const isBeaCommentSeen = useRef(note.isBeaCommentSeen ?? false).current;

    const [beaCommentVisible, dismissBeaComment] = useNudgeStorage({ nudgeId: `bea-comment-${note.noteId}` });

    const recording = note.otherAttributes?.recording as IRecording | undefined;
    const beaComment = recording?.summary?.beaComment;
    const extractedFields = recording?.extractedFields;
    const hasFields = hasExtractedFields(extractedFields);
    const name = extractedFields?.careRecipientName;

    const isProcessing = !!recording && recording.summaryPrompt === 'intro'
        && recording.status !== 'summarized' && recording.status !== 'summarize-failed';

    const hasRealContent = !!beaComment || hasFields;
    const showPlaceholder = isProcessing && !isBeaCommentSeen;
    const showFirstView = !isProcessing && hasRealContent && !isBeaCommentSeen && !firstViewDismissed
        && (hasFields || beaCommentVisible);
    const showFollowup = (isBeaCommentSeen || firstViewDismissed) && beaCommentVisible && hasFields;

    // Mark as seen on the server when the first view is displayed. This drives
    // the transition from the detailed first view (which enumerates extracted fields)
    // to the simpler follow-up message ("I added some details to X's profile"),
    // since extracted data becomes stale as the user edits their profile.
    // The seen state is locked in at mount (above), so this server call only
    // affects future visits / other devices.
    useAsyncEffect(async () => {
        if (showFirstView && !seenMarkedRef.current) {
            try {
                seenMarkedRef.current = true;
                await notesClient.markBeaCommentSeen(note);
                note.isBeaCommentSeen = true;
            } catch {
                // Best-effort: on failure the user simply sees the detailed first view
                // again on their next visit. Reset ref to allow retry on next render.
                seenMarkedRef.current = false;
            }
        }
    }, [showFirstView]);

    if (!showPlaceholder && !showFirstView && !showFollowup) {
        return null;
    }

    const onDismiss = () => {
        setHiding(true);
        setTimeout(() => {
            setHiding(false);
            if (showFirstView) {
                if (hasFields) {
                    setFirstViewDismissed(true);
                } else {
                    dismissBeaComment();
                }
            } else {
                dismissBeaComment();
            }
        }, 500);
    };

    const onViewProfile = () => {
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        history.push(`/care/${careMapId}`);
    };

    const onAddMoreInfo = () => {
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        history.push(`/q/new-care-recipient-2?id=${careMapId}&startFromBeginning=true`);
    };

    // Placeholder: shown while processing is still in progress
    if (showPlaceholder) {
        return (
            <div className={classes.container} data-type="bea-comment-bubble">
                <div className={classes.bubble}>
                    <div className={classes.header}>
                        <img
                            src="/assets/images/help/helper-bea.png"
                            alt="Bea"
                            className={classes.avatar}
                        />
                        <div className={classes.headerInfo}>
                            <div className={classes.name}>Bea</div>
                        </div>
                    </div>
                    <div className={classes.commentText}>
                        {"I'm still reviewing what you shared \u2014 check back in a moment!"}
                    </div>
                </div>
            </div>
        );
    }

    // Followup: simplified message with profile link
    if (showFollowup) {
        const followupText = `I added some details to ${name ? `${name}'s` : 'the'} profile from this recording.`;

        return (
            <div className={`${classes.container} ${hiding ? classes.hiding : ''}`} data-type="bea-comment-bubble">
                <div className={classes.bubble}>
                    <div className={classes.header}>
                        <img
                            src="/assets/images/help/helper-bea.png"
                            alt="Bea"
                            className={classes.avatar}
                        />
                        <div className={classes.headerInfo}>
                            <div className={classes.name}>Bea</div>
                        </div>
                        <IonButton
                            color="medium"
                            fill="clear"
                            shape="round"
                            size="small"
                            onClick={onDismiss}
                            className={classes.closeButton}
                            data-id="bea-comment-close"
                        >
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </div>

                    <div className={classes.commentText}>
                        {followupText}
                    </div>

                    <div className={classes.profileLink} onClick={onViewProfile} data-type="view-profile">
                        {`View ${name ? `${name}'s` : ''} Profile`}
                    </div>
                </div>
            </div>
        );
    }

    // First view: full comment with extracted fields
    const commentText = beaComment
        || (hasFields
            ? `I picked up a few things from your intro that I've added to ${name || 'the care recipient'}'s profile. Take a look and let me know if anything needs updating!`
            : `Welcome to Innerhive! When you're ready, you can bring Bea along to record appointments and get automatic summaries, or set up a Care Recipient Profile to keep everything organized.`);

    const visibleFields = hasFields
        ? FIELD_CONFIG
            .map(({ key, label, Icon }) => {
                const formatted = formatFieldValue(extractedFields![key]);
                return formatted ? { label, value: formatted, Icon } : null;
            })
            .filter(Boolean) as { label: string; value: string; Icon: IconComponent }[]
        : [];

    return (
        <div className={`${classes.container} ${hiding ? classes.hiding : ''}`} data-type="bea-comment-bubble">
            <div className={classes.bubble}>
                <div className={classes.header}>
                    <img
                        src="/assets/images/help/helper-bea.png"
                        alt="Bea"
                        className={classes.avatar}
                    />
                    <div className={classes.headerInfo}>
                        <div className={classes.name}>Bea</div>
                    </div>
                    <IonButton
                        color="medium"
                        fill="clear"
                        shape="round"
                        size="small"
                        onClick={onDismiss}
                        className={classes.closeButton}
                        data-id="bea-comment-close"
                    >
                        <IonIcon slot="icon-only" icon={close} />
                    </IonButton>
                </div>

                <div className={classes.commentText}>
                    <SelectableText>{commentText}</SelectableText>
                </div>

                {visibleFields.length > 0 && (<>
                    <div className={classes.fieldsTitle}>I saved this info from what you shared</div>
                    <div className={classes.fieldsCard}>
                        {visibleFields.map(({ label, value, Icon }) => (
                            <div key={label} className={classes.fieldRow}>
                                <div className={classes.fieldIcon}>
                                    <Icon color="medium" size="small" />
                                </div>
                                <span className={classes.fieldLabel}>{label}</span>
                                <span className={classes.fieldValue}>{value}</span>
                            </div>
                        ))}
                    </div>
                </>)}

                {hasFields && (
                    <div className={classes.buttons}>
                        <IonButton
                            expand="block"
                            color="primary"
                            fill="outline"
                            onClick={onAddMoreInfo}
                            data-type="edit-profile"
                        >
                            Add More Info
                        </IonButton>
                    </div>
                )}
            </div>
        </div>
    );
};
